import gsap from 'gsap'
import dynamic from 'next/dynamic'
import Link from 'next/link'

import type { FC, PointerEvent as ReactPointerEvent, ReactNode } from 'react'
import type { Category, Product } from '@/types'
import type { BoutiqueDollInfo } from '@/components/three/boutique-canvas'
import type { Quality } from '@/components/three/textures'

import { Component, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { formatIDR } from '@/lib/format'
import { useScrollLock } from '@/hooks/use-scroll-lock'
import { useSettings } from '@/components/providers/settings-provider'
import { BrandMark } from '@/components/generals/logo'
import { BoutiqueCatalog } from '@/components/landing/boutique-catalog'
import { BoutiqueFallback } from '@/components/landing/boutique-fallback'
import { BoutiqueLoader } from '@/components/landing/boutique-loader'
import { BoutiqueQuickView } from '@/components/landing/boutique-quick-view'
import { SHELF_NAV } from '@/components/three/boutique-shelves'
import { labelName } from '@/constants/site'

import {
    ArrowRight,
    ChevronDown,
    Hand,
    LayoutGrid,
    Plus,
    Store,
    X
} from 'lucide-react'

import {
    detectQuality,
    prefersReducedMotion,
    supportsWebGL
} from '@/lib/webgl'

const BoutiqueCanvas = dynamic(() => import('@/components/three/boutique-canvas'), { ssr: false, loading: () => null })

class CanvasBoundary extends Component<{ onError: () => void, children: ReactNode }, { failed: boolean }> {
    state = { failed: false }

    static getDerivedStateFromError() {
        return { failed: true }
    }

    componentDidCatch(error: unknown) {
        console.error('The 3D boutique could not start', error)
        this.props.onError()
    }

    render() {
        return this.state.failed ? null : this.props.children
    }
}

type Status = 'idle' | 'loading' | 'ready' | 'fallback'

const INFO_LABELS: Record<string, string> = {
    'info:sign': 'Cerita kami',
    'info:yarn': 'Pilihan warna'
}

interface BoutiqueHeroProps {
    products: Array<Product>
    categories: Array<Category>
}

export const BoutiqueHero: FC<BoutiqueHeroProps> = ({ products, categories }): ReactNode => {
    const settings = useSettings()
    const [status, setStatus] = useState<Status>('idle')
    const [quality, setQuality] = useState<Quality>('medium')
    const [assetProgress, setAssetProgress] = useState(0)
    const [warmup, setWarmup] = useState(0)
    const [loaderGone, setLoaderGone] = useState(false)
    const [mode, setMode] = useState<'intro' | 'explore'>('intro')
    const [focusKey, setFocusKey] = useState<string | null>(null)
    const [catalogProductId, setCatalogProductId] = useState<string | null>(null)
    const [catalogOpen, setCatalogOpen] = useState(false)
    const [shelfId, setShelfId] = useState<string | null>(null)
    const [hoverKey, setHoverKey] = useState<string | null>(null)
    const [dolls, setDolls] = useState<Array<BoutiqueDollInfo>>([])
    const [colorIndex, setColorIndex] = useState(0)
    const [inView, setInView] = useState(true)
    const [reducedMotion, setReducedMotion] = useState(false)
    const [viewport, setViewport] = useState({ width: 1280, height: 800 })
    const [showHint, setShowHint] = useState(false)

    const hotspotElements = useRef(new Map<string, HTMLElement>())
    const sectionRef = useRef<HTMLElement>(null)
    const copyRef = useRef<HTMLDivElement>(null)
    const tooltipRef = useRef<HTMLDivElement>(null)

    const productsById = useMemo(() => new Map(products.map(product => [product.id, product])), [products])
    const categoryNames = useMemo(() => Object.fromEntries(categories.map(category => [category.slug, category.name])), [categories])
    const categoriesBySlug = useMemo(() => new Map(categories.map(category => [category.slug, category])), [categories])
    const isDesktop = viewport.width >= 1024

    // Decide on mount whether this device gets the 3D boutique or the illustrated fallback.
    useEffect(() => {
        setReducedMotion(prefersReducedMotion())

        const update = () => setViewport({ width: window.innerWidth, height: window.innerHeight })

        update()
        window.addEventListener('resize', update)

        if (supportsWebGL()) {
            setQuality(detectQuality())
            setStatus('loading')
        }
        else {
            setStatus('fallback')
        }

        return () => window.removeEventListener('resize', update)
    }, [])

    // Gentle progress while the 3D code downloads, before real asset progress arrives.
    useEffect(() => {
        if (status !== 'loading') return

        const timer = window.setInterval(() => {
            setWarmup(value => Math.min(0.22, value + (0.22 - value) * 0.07 + 0.003))
        }, 120)

        const giveUp = window.setTimeout(() => setStatus(current => (current === 'loading' ? 'fallback' : current)), 30000)

        return () => {
            window.clearInterval(timer)
            window.clearTimeout(giveUp)
        }
    }, [status])

    // Pause rendering while the boutique is scrolled out of view.
    useEffect(() => {
        const node = sectionRef.current

        if (!node || !('IntersectionObserver' in window)) return

        const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold: 0.01 })

        observer.observe(node)

        return () => observer.disconnect()
    }, [])

    useEffect(() => {
        if (status === 'fallback') setLoaderGone(true)
    }, [status])

    // Reveal the headline once the loader has lifted.
    useEffect(() => {
        const node = copyRef.current

        if (!node || !(loaderGone || status === 'fallback')) return

        const items = node.querySelectorAll('[data-hero-reveal]')

        if (reducedMotion) {
            gsap.set(items, { autoAlpha: 1, y: 0 })

            return
        }

        const tween = gsap.fromTo(items, { autoAlpha: 0, y: 32 }, { autoAlpha: 1, y: 0, duration: 1.1, stagger: 0.12, ease: 'power3.out', delay: 0.1 })

        return () => {
            tween.kill()
        }
    }, [loaderGone, status, reducedMotion])

    useScrollLock(mode === 'explore')

    useEffect(() => {
        if (!showHint) return

        const timer = window.setTimeout(() => setShowHint(false), 5200)

        return () => window.clearTimeout(timer)
    }, [showHint])

    const enterExplore = useCallback(() => {
        if (window.scrollY > 2) window.scrollTo({ top: 0 })

        setMode('explore')
        setShowHint(true)
    }, [])

    const leaveExplore = useCallback(() => {
        setFocusKey(null)
        setCatalogProductId(null)
        setCatalogOpen(false)
        setShelfId(null)
        setHoverKey(null)
        setMode('intro')
    }, [])

    const handleSelect = useCallback((key: string) => {
        const info = dolls.find(doll => doll.key === key)

        if (!info) return

        if (mode !== 'explore') enterExplore()

        setShowHint(false)
        setHoverKey(null)
        setColorIndex(0)
        setCatalogProductId(null)
        setCatalogOpen(false)
        setFocusKey(key)
        setShelfId(info.shelfId)
    }, [dolls, mode, enterExplore])

    // From the catalogue panel: pick up the doll when it stands on a shelf, otherwise show its photos.
    const handleCatalogPick = useCallback((product: Product) => {
        const placed = dolls.find(doll => doll.productId === product.id && doll.kind !== 'pedestal')
            ?? dolls.find(doll => doll.productId === product.id)

        if (placed) {
            handleSelect(placed.key)

            return
        }

        setShowHint(false)
        setColorIndex(0)
        setFocusKey(null)
        setCatalogOpen(false)
        setCatalogProductId(product.id)
    }, [dolls, handleSelect])

    const closeFocus = useCallback(() => {
        setFocusKey(null)
        setCatalogProductId(null)
    }, [])

    const focusInfo = focusKey ? dolls.find(doll => doll.key === focusKey) ?? null : null
    const focusProduct = focusInfo ? productsById.get(focusInfo.productId) ?? null : null
    const catalogProduct = catalogProductId ? productsById.get(catalogProductId) ?? null : null
    const panelProduct = focusProduct ?? catalogProduct
    const shelfmates = focusInfo ? dolls.filter(doll => doll.shelfId === focusInfo.shelfId) : []
    const focusIndex = focusInfo ? shelfmates.findIndex(doll => doll.key === focusInfo.key) : -1

    const goPrevious = focusIndex > 0 ? () => handleSelect(shelfmates[focusIndex - 1].key) : null
    const goNext = focusIndex >= 0 && focusIndex < shelfmates.length - 1 ? () => handleSelect(shelfmates[focusIndex + 1].key) : null

    // Keyboard: Escape closes the doll, then the catalogue, then leaves the boutique; arrows browse the shelf.
    useEffect(() => {
        if (mode !== 'explore') return

        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                if (focusKey || catalogProductId) closeFocus()
                else if (catalogOpen) setCatalogOpen(false)
                else leaveExplore()
            }

            if (focusKey && event.key === 'ArrowLeft' && goPrevious) goPrevious()
            if (focusKey && event.key === 'ArrowRight' && goNext) goNext()
        }

        window.addEventListener('keydown', onKey)

        return () => window.removeEventListener('keydown', onKey)
    }, [mode, focusKey, catalogProductId, catalogOpen, closeFocus, leaveExplore, goPrevious, goNext])

    const handleInfo = (key: string) => {
        if (key === 'info:sign') {
            leaveExplore()
            window.setTimeout(() => document.getElementById('story')?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth' }), 80)
        }

        if (key === 'info:yarn') setShelfId('custom')
    }

    const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
        const tooltip = tooltipRef.current

        if (!tooltip) return

        const bounds = event.currentTarget.getBoundingClientRect()

        tooltip.style.transform = `translate3d(${event.clientX - bounds.left + 18}px, ${event.clientY - bounds.top + 18}px, 0)`
    }

    // Stable ref callbacks per hotspot so elements are registered once, not on every render.
    const refCallbacks = useRef(new Map<string, (element: HTMLElement | null) => void>())

    const registerHotspot = useCallback((key: string) => {
        let callback = refCallbacks.current.get(key)

        if (!callback) {
            callback = (element: HTMLElement | null) => {
                if (element) hotspotElements.current.set(key, element)
                else hotspotElements.current.delete(key)
            }

            refCallbacks.current.set(key, callback)
        }

        return callback
    }, [])

    const hoverInfo = hoverKey ? dolls.find(doll => doll.key === hoverKey) ?? null : null
    const hoverProduct = hoverInfo ? productsById.get(hoverInfo.productId) ?? null : null
    const colorOverride = focusProduct && colorIndex > 0 ? focusProduct.colors[colorIndex]?.hex ?? null : null
    const panelInset = useMemo(() => ({
        right: isDesktop ? 440 : 0,
        bottom: isDesktop ? 0 : Math.min(viewport.height * 0.6, 520) + 8
    }), [isDesktop, viewport.height])

    const shelvesOnDisplay = useMemo(() => new Set(dolls.map(doll => doll.shelfId)), [dolls])
    const onDisplay = useMemo(() => new Set(dolls.map(doll => doll.productId)), [dolls])
    const shelfNav = SHELF_NAV.filter(entry => shelvesOnDisplay.has(entry.id))
    const shelfCategory = SHELF_NAV.find(entry => entry.id === shelfId)?.categorySlug ?? null
    const hotspots = dolls.filter(doll => doll.featured)
    const exploring = mode === 'explore'
    const ready = status === 'ready'
    const displayProgress = ready ? 1 : Math.max(warmup, 0.22 + assetProgress * 0.76)

    const handleProgress = useCallback((value: number) => setAssetProgress(value), [])
    const handleReady = useCallback((info: Array<BoutiqueDollInfo>) => {
        setDolls(info)
        setStatus(current => (current === 'fallback' ? current : 'ready'))
    }, [])
    const handleFailed = useCallback(() => {
        setStatus('fallback')
        setMode('intro')
        setFocusKey(null)
        setCatalogProductId(null)
    }, [])
    const handleHover = useCallback((key: string | null) => setHoverKey(key), [])
    const handleIntroDone = useCallback(() => undefined, [])

    const settingsTag = (column: string) => (settings.id ? `store_settings:${settings.id}:${column}` : undefined)

    const hotspotTitle = (product: Product): string | null => {
        if (product.badge) return null

        return product.labels[0] ? labelName(product.labels[0]) : null
    }

    return (
        <section
            ref={sectionRef}
            id='boutique'
            aria-label={`Butik 3D ${settings.storeName}`}
            className='relative h-[100svh] min-h-[600px] w-full'
        >
            <div
                className={cn(
                    'overflow-hidden bg-[#e9d9c5]',
                    exploring ? 'fixed inset-0 z-[70]' : 'absolute inset-0'
                )}
                onPointerMove={handlePointerMove}
                style={{ cursor: hoverKey ? 'pointer' : exploring && !focusKey ? 'grab' : 'default' }}
            >
                {(status === 'fallback' || status === 'idle') && <BoutiqueFallback />}

                {(status === 'loading' || status === 'ready') && (
                    <div className='absolute inset-0'>
                        <CanvasBoundary onError={handleFailed}>
                            <BoutiqueCanvas
                                quality={quality}
                                products={products}
                                categoryNames={categoryNames}
                                signTitle={settings.storeName}
                                signSubtitle={settings.tagline}
                                mode={exploring ? 'explore' : 'intro'}
                                focusKey={focusKey}
                                shelfId={exploring ? shelfId : null}
                                hoverKey={hoverKey}
                                colorOverride={colorOverride}
                                reducedMotion={reducedMotion}
                                active={inView || exploring}
                                panelInset={panelInset}
                                hotspotElements={hotspotElements}
                                onProgress={handleProgress}
                                onReady={handleReady}
                                onFailed={handleFailed}
                                onHover={handleHover}
                                onSelect={handleSelect}
                                onDismiss={closeFocus}
                                onIntroDone={handleIntroDone}
                            />
                        </CanvasBoundary>
                    </div>
                )}

                {/* Soft vignette for a warm, cinematic frame */}
                <div className='pointer-events-none absolute inset-0 bg-[radial-gradient(120%_85%_at_50%_42%,transparent_55%,rgba(58,40,30,0.28)_100%)]' />

                {/* Hotspots, positioned every frame by the 3D scene */}
                {ready && exploring && !focusKey && !catalogProductId && (
                    <div className='pointer-events-none absolute inset-0 z-20'>
                        {hotspots.map(doll => {
                            const product = productsById.get(doll.productId)

                            if (!product) return null

                            const title = hotspotTitle(product)

                            return (
                                <div key={doll.key} ref={registerHotspot(doll.key)} data-onscreen='false' className='group absolute left-0 top-0 transition-opacity duration-300 data-[onscreen=false]:pointer-events-none data-[onscreen=false]:opacity-0'>
                                    <button
                                        type='button'
                                        onClick={() => handleSelect(doll.key)}
                                        className='pointer-events-auto relative -translate-x-1/2 -translate-y-1/2 rounded-full focus-visible:outline-none'
                                        aria-label={`Lihat ${product.name}`}
                                    >
                                        <span className='relative grid size-9 place-items-center'>
                                            <span className='absolute inset-1 rounded-full bg-white/70 motion-safe:animate-hotspot' />
                                            <span className='relative grid size-7 place-items-center rounded-full bg-white text-blush-600 shadow-[0_4px_14px_rgba(58,40,30,0.25)] ring-2 ring-blush-200 transition group-hover:scale-110 group-focus-within:ring-caramel-400'>
                                                <Plus className='size-4' />
                                            </span>
                                        </span>
                                        <span className='pointer-events-none absolute left-1/2 top-full mt-1.5 -translate-x-1/2 whitespace-nowrap rounded-full bg-cocoa-900/85 px-3 py-1 text-xs font-semibold text-cream-50 opacity-0 shadow-lg transition group-hover:opacity-100 group-focus-within:opacity-100'>
                                            {product.badge && (
                                                <>
                                                    <span data-mdlfy-cms={`products:${product.id}:badge`}>
                                                        {product.badge}
                                                    </span>
                                                    <span aria-hidden='true'> · </span>
                                                </>
                                            )}
                                            {title && (
                                                <>
                                                    <span>{title}</span>
                                                    <span aria-hidden='true'> · </span>
                                                </>
                                            )}
                                            <span data-mdlfy-cms={`products:${product.id}:name`}>
                                                {product.name}
                                            </span>
                                        </span>
                                    </button>
                                </div>
                            )
                        })}

                        {Object.entries(INFO_LABELS).map(([key, label]) => (
                            <div key={key} ref={registerHotspot(key)} data-onscreen='false' className='group absolute left-0 top-0 transition-opacity duration-300 data-[onscreen=false]:pointer-events-none data-[onscreen=false]:opacity-0'>
                                <button
                                    type='button'
                                    onClick={() => handleInfo(key)}
                                    className='pointer-events-auto inline-flex -translate-x-1/2 -translate-y-1/2 items-center gap-1.5 rounded-full bg-cocoa-800/90 px-3.5 py-1.5 text-xs font-semibold text-cream-50 shadow-lg backdrop-blur transition hover:bg-cocoa-700'
                                >
                                    <span className='size-1.5 rounded-full bg-blush-200 motion-safe:animate-pulse' />
                                    {label}
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {/* Hover tooltip (desktop pointers) */}
                <div
                    ref={tooltipRef}
                    aria-hidden='true'
                    className={cn(
                        'pointer-events-none absolute left-0 top-0 z-30 hidden rounded-2xl bg-cream-50/95 px-4 py-2.5 shadow-[0_12px_30px_-12px_rgba(58,40,30,0.45)] backdrop-blur transition-opacity duration-200 lg:block',
                        hoverProduct && !focusKey ? 'opacity-100' : 'opacity-0'
                    )}
                >
                    {hoverProduct && (
                        <>
                            <p className='font-heading text-base text-cocoa-900'>
                                <span data-mdlfy-cms={`products:${hoverProduct.id}:name`}>
                                    {hoverProduct.name}
                                </span>
                            </p>
                            <p className='text-xs font-semibold text-cocoa-600'>
                                {formatIDR(hoverProduct.price)}
                                <span className='text-cocoa-500'> · Klik untuk mengambil</span>
                            </p>
                        </>
                    )}
                </div>

                {/* Intro copy */}
                <div
                    ref={copyRef}
                    className={cn(
                        'cw-hero-copy pointer-events-none absolute inset-x-0 bottom-0 z-20 transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]',
                        exploring && 'translate-y-6 opacity-0'
                    )}
                    inert={exploring}
                >
                    <div className='absolute inset-x-0 bottom-0 h-[80%] bg-gradient-to-t from-[#f8ebe0] via-[#f8ebe0]/80 to-transparent lg:h-[80%] lg:bg-[linear-gradient(to_top_right,#f8ebe0_10%,rgba(248,235,224,0.84)_40%,transparent_72%)]' />
                    <div className='relative mx-auto w-full max-w-7xl px-5 pb-[max(2.25rem,env(safe-area-inset-bottom))] sm:px-8 lg:pb-14'>
                        <div className='max-w-[40rem]'>
                            <div data-hero-reveal className='mb-5 flex items-center gap-4 opacity-0'>
                                <BrandMark size={viewport.width >= 640 ? 104 : 72} priority className='shadow-[0_18px_40px_-18px_rgba(58,40,30,0.6)] ring-4 ring-white/80' />
                                <p className='inline-flex flex-col gap-1'>
                                    <span className='inline-flex items-center gap-2 self-start rounded-full border border-cocoa-700/10 bg-white/70 px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.24em] text-cocoa-600 backdrop-blur'>
                                        <span className='size-1.5 rounded-full bg-blush-400' />
                                        {settings.subtagline
                                            ? <span data-mdlfy-cms={settingsTag('subtagline')}>{settings.subtagline}</span>
                                            : 'Handmade Crochet'}
                                    </span>
                                    <span className='pl-1 text-sm font-semibold text-cocoa-700'>
                                        Butik boneka rajut 3D
                                    </span>
                                </p>
                            </div>
                            <h1 data-hero-reveal className='font-heading text-[clamp(2.9rem,8vw,6rem)] font-medium leading-[0.92] tracking-[-0.02em] text-cocoa-900 opacity-0 font-soft'>
                                Little Charms,
                                <br />
                                <span className='italic text-blush-600'>Big Love</span>
                            </h1>
                            <p data-hero-reveal className='mt-4 max-w-xl text-base leading-relaxed text-cocoa-700 opacity-0 sm:mt-5 sm:text-xl'>
                                Boneka rajut, gantungan kunci, dan kerajinan lucu dari <span className='font-semibold' data-mdlfy-cms={settingsTag('store_name')}>{settings.storeName}</span>, dibuat satu per satu dengan ketelitian dan penuh cinta.
                            </p>
                            <div data-hero-reveal className='pointer-events-auto mt-8 flex flex-wrap items-center gap-3 opacity-0'>
                                {status === 'fallback'
                                    ? (
                                        <Link
                                            href='/shop'
                                            className='inline-flex h-12 items-center gap-2.5 rounded-full bg-cocoa-800 px-5 text-[15px] font-semibold text-cream-50 shadow-[0_18px_40px_-16px_rgba(58,40,30,0.8)] transition hover:-translate-y-0.5 hover:bg-cocoa-700 sm:h-14 sm:px-7 sm:text-base'
                                        >
                                            Jelajahi Koleksi
                                            <ArrowRight className='size-5' />
                                        </Link>
                                    )
                                    : (
                                        <button
                                            type='button'
                                            onClick={enterExplore}
                                            disabled={!ready}
                                            className='inline-flex h-12 items-center gap-2.5 rounded-full bg-cocoa-800 px-5 text-[15px] font-semibold text-cream-50 shadow-[0_18px_40px_-16px_rgba(58,40,30,0.8)] transition hover:-translate-y-0.5 hover:bg-cocoa-700 disabled:opacity-70 sm:h-14 sm:px-7 sm:text-base'
                                        >
                                            Jelajahi Koleksi
                                            <ArrowRight className='size-5' />
                                        </button>
                                    )}
                                <Link
                                    href='/shop'
                                    className='inline-flex h-12 items-center gap-2 rounded-full border border-cocoa-700/15 bg-white/70 px-5 text-[15px] font-semibold text-cocoa-800 backdrop-blur transition hover:bg-white sm:h-14 sm:px-6 sm:text-base'
                                >
                                    <LayoutGrid className='size-5' />
                                    Katalog 2D
                                </Link>
                            </div>
                        </div>
                    </div>

                    <a
                        href='#collections'
                        className='pointer-events-auto absolute bottom-6 right-8 hidden items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-cocoa-600 transition hover:text-cocoa-900 lg:flex'
                    >
                        Gulir untuk melihat
                        <ChevronDown className='size-4 motion-safe:animate-bounce' />
                    </a>
                </div>

                {/* Explore mode chrome */}
                <div
                    className={cn(
                        'pointer-events-none absolute inset-0 z-20 transition-opacity duration-500',
                        exploring ? 'opacity-100' : 'opacity-0'
                    )}
                    inert={!exploring}
                >
                    <div className='flex items-start justify-between gap-3 p-4 sm:p-6'>
                        <p className='pointer-events-auto inline-flex items-center gap-2 rounded-full bg-cream-50/85 py-1.5 pl-1.5 pr-4 text-sm font-semibold text-cocoa-800 shadow-sm backdrop-blur'>
                            <BrandMark size={30} />
                            <span className='hidden sm:inline'>Selamat datang di butik</span>
                            <span data-mdlfy-cms={settingsTag('store_name')}>{settings.storeName}</span>
                        </p>
                        <div className='pointer-events-auto flex flex-wrap justify-end gap-2'>
                            <button
                                type='button'
                                onClick={() => setCatalogOpen(open => !open)}
                                aria-expanded={catalogOpen}
                                className={cn(
                                    'inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold shadow-lg backdrop-blur transition',
                                    catalogOpen ? 'bg-blush-500 text-white hover:bg-blush-600' : 'bg-cream-50/90 text-cocoa-800 hover:bg-white'
                                )}
                            >
                                <Store className='size-4' />
                                Katalog
                            </button>
                            <button
                                type='button'
                                onClick={leaveExplore}
                                className='inline-flex items-center gap-2 rounded-full bg-cocoa-900/85 px-4 py-2 text-sm font-semibold text-cream-50 shadow-lg backdrop-blur transition hover:bg-cocoa-800'
                            >
                                <X className='size-4' />
                                <span className='hidden sm:inline'>Keluar dari butik</span>
                                <span className='sm:hidden'>Keluar</span>
                            </button>
                        </div>
                    </div>

                    <div
                        className={cn(
                            'pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transition-all duration-500',
                            showHint && exploring && !focusKey ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
                        )}
                        aria-live='polite'
                    >
                        <p className='inline-flex items-center gap-2.5 rounded-full bg-cocoa-900/80 px-5 py-3 text-center text-sm font-semibold text-cream-50 shadow-xl backdrop-blur'>
                            <Hand className='size-5 shrink-0 text-blush-200' />
                            Geser untuk melihat sekeliling, lalu ketuk boneka untuk mengambilnya
                        </p>
                    </div>

                    <nav
                        aria-label='Rak butik'
                        className={cn(
                            'absolute inset-x-0 bottom-0 px-3 pb-[max(1rem,env(safe-area-inset-bottom))] transition-all duration-500 sm:px-6',
                            focusKey || catalogProductId || catalogOpen ? 'pointer-events-none translate-y-6 opacity-0' : 'translate-y-0 opacity-100'
                        )}
                    >
                        <div className='pointer-events-auto no-scrollbar mx-auto flex max-w-max gap-2 overflow-x-auto rounded-full bg-cream-50/85 p-1.5 shadow-[0_18px_40px_-20px_rgba(58,40,30,0.6)] backdrop-blur'>
                            <button
                                type='button'
                                onClick={() => setShelfId(null)}
                                aria-pressed={!shelfId}
                                className={cn(
                                    'shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition',
                                    !shelfId ? 'bg-cocoa-800 text-cream-50' : 'text-cocoa-700 hover:bg-white'
                                )}
                            >
                                Seluruh butik
                            </button>
                            {shelfNav.map(entry => {
                                const category = entry.categorySlug ? categoriesBySlug.get(entry.categorySlug) : undefined
                                const active = shelfId === entry.id

                                return (
                                    <button
                                        key={entry.id}
                                        type='button'
                                        onClick={() => setShelfId(entry.id)}
                                        aria-pressed={active}
                                        className={cn(
                                            'shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition',
                                            active ? 'bg-cocoa-800 text-cream-50' : 'text-cocoa-700 hover:bg-white'
                                        )}
                                    >
                                        {category
                                            ? <span data-mdlfy-cms={`categories:${category.id}:name`}>{category.name}</span>
                                            : entry.fallback}
                                    </button>
                                )
                            })}
                        </div>
                    </nav>
                </div>

                <BoutiqueCatalog
                    open={exploring && catalogOpen && !panelProduct}
                    products={products}
                    categories={categories}
                    shelfCategory={shelfCategory}
                    onDisplay={onDisplay}
                    onPick={handleCatalogPick}
                    onClose={() => setCatalogOpen(false)}
                />

                <BoutiqueQuickView
                    product={panelProduct}
                    inScene={Boolean(focusProduct)}
                    open={Boolean(panelProduct) && exploring}
                    colorIndex={colorIndex}
                    onColorChange={setColorIndex}
                    onClose={closeFocus}
                    onPrevious={focusProduct ? goPrevious : null}
                    onNext={focusProduct ? goNext : null}
                />

                {!loaderGone && (
                    <BoutiqueLoader
                        progress={displayProgress}
                        done={ready}
                        reducedMotion={reducedMotion}
                        onExited={() => setLoaderGone(true)}
                    />
                )}

                <p className='sr-only'>
                    Butik 3D interaktif tempat kamu bisa melihat-lihat rak dan mengambil setiap boneka rajut. Semua produk juga tersedia di katalog di bawah.
                </p>
            </div>
        </section>
    )
}
